import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  CalendarIcon, 
  Plus, 
  Trash2, 
  Pencil, 
  Receipt,
  DollarSign,
  Store,
  Search,
  FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CommissionIncomePage() {
  const { user } = useAuth();
  const { stores, selectedStore, setSelectedStore } = useStore();
  
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [commissions, setCommissions] = useState([]);
  const [commissionTypes, setCommissionTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // View mode: 'day' or 'range'
  const [viewMode, setViewMode] = useState('day');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  
  // Form state
  const [newCommission, setNewCommission] = useState({
    commission_date: format(new Date(), 'yyyy-MM-dd'),
    commission_type: '',
    description: '',
    amount: '',
    payment_type: 'cash'
  });
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);
  
  useEffect(() => {
    if (stores.length > 0) {
      const storeToSelect = selectedStore || stores[0];
      setSelectedStoreId(storeToSelect.id);
    }
  }, [stores, selectedStore]);

  useEffect(() => {
    fetchCommissionTypes();
  }, []);

  useEffect(() => {
    if (selectedStoreId) {
      fetchCommissions();
    }
  }, [selectedStoreId, selectedDate, viewMode, startDate, endDate]);

  const fetchCommissionTypes = async () => {
    try {
      const response = await axios.get(`${API}/api/commissions/types`);
      setCommissionTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch commission types:', error);
    }
  };

  const fetchCommissions = async () => {
    setLoading(true);
    try {
      let url = `${API}/api/commissions?store_id=${selectedStoreId}`;
      
      if (viewMode === 'day') {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        url += `&start_date=${dateStr}&end_date=${dateStr}`;
      } else {
        url += `&start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`;
      }
      
      const response = await axios.get(url);
      setCommissions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
      toast.error('Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  const handleStoreChange = (storeId) => {
    setSelectedStoreId(storeId);
    const store = stores.find(s => s.id === storeId);
    if (store) {
      setSelectedStore(store);
    }
  };

  const handleAmountFocus = (e) => {
    e.target.select();
  };

  const handleCreateCommission = async () => {
    if (!newCommission.commission_type) {
      toast.error('Please select a commission type');
      return;
    }
    if (!newCommission.amount || parseFloat(newCommission.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      const commType = commissionTypes.find(t => t.id === newCommission.commission_type);
      
      await axios.post(`${API}/api/commissions`, {
        store_id: selectedStoreId,
        commission_date: newCommission.commission_date,
        commission_type: newCommission.commission_type,
        description: newCommission.description || commType?.name || '',
        amount: parseFloat(newCommission.amount),
        payment_type: newCommission.payment_type
      });
      
      toast.success('Commission added successfully');
      fetchCommissions();
      setNewCommission({
        commission_date: format(selectedDate, 'yyyy-MM-dd'),
        commission_type: '',
        description: '',
        amount: ''
      });
    } catch (error) {
      console.error('Failed to add commission:', error);
      toast.error(error.response?.data?.detail || 'Failed to add commission');
    }
  };

  const handleEditCommission = async () => {
    if (!editingCommission) return;
    
    try {
      await axios.put(`${API}/api/commissions/${editingCommission.id}`, {
        commission_type: editingCommission.commission_type,
        description: editingCommission.description,
        amount: parseFloat(editingCommission.amount)
      });
      
      toast.success('Commission updated');
      setEditDialogOpen(false);
      setEditingCommission(null);
      fetchCommissions();
    } catch (error) {
      console.error('Failed to update commission:', error);
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDeleteCommission = async (commissionId) => {
    if (!window.confirm('Are you sure you want to delete this commission entry?')) return;
    
    try {
      await axios.delete(`${API}/api/commissions/${commissionId}`);
      toast.success('Commission deleted');
      fetchCommissions();
    } catch (error) {
      console.error('Failed to delete commission:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const openEditDialog = (commission) => {
    setEditingCommission({ ...commission });
    setEditDialogOpen(true);
  };

  const totalCommission = commissions.reduce((sum, c) => sum + (c.amount || 0), 0);

  // Group commissions by type for summary
  const commissionsByType = commissions.reduce((acc, c) => {
    const type = c.commission_type;
    if (!acc[type]) {
      acc[type] = { count: 0, total: 0 };
    }
    acc[type].count++;
    acc[type].total += c.amount || 0;
    return acc;
  }, {});

  const canEdit = user?.user_type === 'super_user' || user?.user_type === 'admin' || user?.user_type === 'store_manager';
  const canDelete = user?.user_type === 'super_user' || user?.user_type === 'admin';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
            Commission Income
          </h1>
          <p className="text-muted-foreground">Record and manage commission income from services</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Store Selector */}
            <div className="space-y-2">
              <Label>Store</Label>
              <Select value={selectedStoreId} onValueChange={handleStoreChange}>
                <SelectTrigger className="w-[220px]">
                  <Store className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Select store" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* View Mode */}
            <div className="space-y-2">
              <Label>View</Label>
              <Select value={viewMode} onValueChange={setViewMode}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Single Day</SelectItem>
                  <SelectItem value="range">Date Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date Picker(s) */}
            {viewMode === 'day' ? (
              <div className="space-y-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[200px] justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(selectedDate, 'dd MMM yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        if (date) {
                          setSelectedDate(date);
                          setNewCommission(prev => ({ ...prev, commission_date: format(date, 'yyyy-MM-dd') }));
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>From</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[160px] justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(startDate, 'dd MMM yyyy')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={(date) => date && setStartDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>To</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[160px] justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(endDate, 'dd MMM yyyy')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={(date) => date && setEndDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Add New Commission */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <Plus className="h-5 w-5 text-primary" />
              Add Commission
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {newCommission.commission_date ? format(new Date(newCommission.commission_date), 'dd MMM yyyy') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={newCommission.commission_date ? new Date(newCommission.commission_date) : undefined}
                    onSelect={(date) => date && setNewCommission(prev => ({ ...prev, commission_date: format(date, 'yyyy-MM-dd') }))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Commission Type *</Label>
              <Select
                value={newCommission.commission_type}
                onValueChange={(val) => {
                  const type = commissionTypes.find(t => t.id === val);
                  setNewCommission(prev => ({ 
                    ...prev, 
                    commission_type: val,
                    description: type?.name || ''
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {[...commissionTypes].sort((a, b) => a.name.localeCompare(b.name)).map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newCommission.description}
                onChange={(e) => setNewCommission(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Additional details..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Amount *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                <Input
                  type="text"
                  value={newCommission.amount}
                  onChange={(e) => {
                    if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                      setNewCommission(prev => ({ ...prev, amount: e.target.value }));
                    }
                  }}
                  onFocus={handleAmountFocus}
                  className="pl-7 font-mono"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Payment Type *</Label>
              <Select
                value={newCommission.payment_type}
                onValueChange={(val) => setNewCommission(prev => ({ ...prev, payment_type: val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select payment type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank">Bank</SelectItem>
                  <SelectItem value="voucher">Voucher</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {newCommission.payment_type === 'cash' && 'Commission will be added to Cash Holding'}
                {newCommission.payment_type === 'bank' && 'Commission will be added to Bank Balance'}
                {newCommission.payment_type === 'voucher' && 'Commission will be added to Voucher Balance'}
              </p>
            </div>

            <Button onClick={handleCreateCommission} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Add Commission
            </Button>
          </CardContent>
        </Card>

        {/* Commission List & Summary */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Barlow Condensed' }}>
              <span className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-primary" />
                Commission Records
              </span>
              <span className="text-lg font-mono text-green-500">
                Total: £{totalCommission.toFixed(2)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : commissions.length > 0 ? (
              <>
                {/* Summary by Type */}
                {Object.keys(commissionsByType).length > 1 && (
                  <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                    <h4 className="text-sm font-medium mb-2">Summary by Type</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {Object.entries(commissionsByType).map(([type, data]) => {
                        const typeName = commissionTypes.find(t => t.id === type)?.name || type;
                        return (
                          <div key={type} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{typeName}</span>
                            <span className="font-mono">£{data.total.toFixed(2)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Table */}
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {commissions.map(commission => {
                        const typeName = commissionTypes.find(t => t.id === commission.commission_type)?.name || commission.commission_type;
                        const paymentLabel = commission.payment_type === 'cash' ? 'Cash' : commission.payment_type === 'bank' ? 'Bank' : commission.payment_type === 'voucher' ? 'Voucher' : 'Cash';
                        return (
                          <TableRow key={commission.id}>
                            <TableCell>{format(new Date(commission.commission_date), 'dd MMM')}</TableCell>
                            <TableCell>
                              <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs">
                                {typeName}
                              </span>
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate">
                              {commission.description || '-'}
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded text-xs ${
                                commission.payment_type === 'cash' ? 'bg-green-500/20 text-green-500' :
                                commission.payment_type === 'bank' ? 'bg-blue-500/20 text-blue-500' :
                                commission.payment_type === 'voucher' ? 'bg-pink-500/20 text-pink-500' :
                                'bg-green-500/20 text-green-500'
                              }`}>
                                {paymentLabel}
                              </span>
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              £{commission.amount?.toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {canEdit && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => openEditDialog(commission)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                )}
                                {canDelete && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="text-red-500 hover:text-red-600"
                                    onClick={() => handleDeleteCommission(commission.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No commission records found for the selected period</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Commission</DialogTitle>
            <DialogDescription>
              Update commission details
            </DialogDescription>
          </DialogHeader>
          {editingCommission && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Commission Type</Label>
                <Select
                  value={editingCommission.commission_type}
                  onValueChange={(val) => setEditingCommission(prev => ({ ...prev, commission_type: val }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...commissionTypes].sort((a, b) => a.name.localeCompare(b.name)).map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingCommission.description || ''}
                  onChange={(e) => setEditingCommission(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                  <Input
                    type="text"
                    value={editingCommission.amount}
                    onChange={(e) => {
                      if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                        setEditingCommission(prev => ({ ...prev, amount: e.target.value }));
                      }
                    }}
                    className="pl-7 font-mono"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEditCommission}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
